/**
 * Conversation visibility ("ocultar conversaciones a los humanos") — the
 * dashboard's read side of the feature flag that `/verifyToken` sends as
 * `user.conversationVisibility: { enabled, exempt }`.
 *
 * Everything here is fail-closed towards "feature off": an absent, malformed
 * or partial value means the dashboard behaves exactly as before the feature
 * existed (no extra Pusher channel, no extra GraphQL fields, no controls).
 */

const DISABLED = Object.freeze({ enabled: false, exempt: false });

/** Normalized `{ enabled, exempt }`; only a literal `true` counts. */
export const getConversationVisibility = (userData) => {
  const raw = userData && userData.conversationVisibility;
  if (!raw || typeof raw !== "object") return DISABLED;
  return { enabled: raw.enabled === true, exempt: raw.exempt === true };
};

// Switch for the configuration UI: the Settings tab and the chat header
// "Oculta a humanos / Mostrar a humanos" control. Off for everyone, even with
// the flag on: for now iVentas sets the rules and viewableForHumans via API.
// Turning it on is a deliberate code change.
const CONVERSATION_VISIBILITY_UI_ENABLED = false;

export const isConversationVisibilityUiEnabled = () => CONVERSATION_VISIBILITY_UI_ENABLED;

export const isConversationVisibilityEnabled = (userData) =>
  getConversationVisibility(userData).enabled;

/** Exempt users see hidden conversations; meaningful only when enabled. */
export const isConversationVisibilityExempt = (userData) => {
  const { enabled, exempt } = getConversationVisibility(userData);
  return enabled && exempt;
};

/**
 * The private Pusher channel where hidden-conversation events arrive, or null
 * when this user must not subscribe to it.
 */
export const hiddenChannelNameFor = (userData) => {
  const userId = userData && userData.id;
  if (!userId || typeof userId !== "string") return null;
  if (!isConversationVisibilityExempt(userData)) return null;
  return `private-hidden-${userId}`;
};

/**
 * Copies `hiddenFromHumans` / `viewableForHumans` from a server node, only
 * when present, so mapped conversations stay identical when the backend does
 * not send them.
 */
export const pickVisibilityFields = (node) => {
  const picked = {};
  if (!node || typeof node !== "object") return picked;
  if (typeof node.hiddenFromHumans === "boolean") {
    picked.hiddenFromHumans = node.hiddenFromHumans;
  }
  if (typeof node.viewableForHumans === "boolean") {
    picked.viewableForHumans = node.viewableForHumans;
  }
  return picked;
};

/**
 * Applies `flags` (only its boolean `hiddenFromHumans` / `viewableForHumans`)
 * to the conversation `clientId` in `data` and in `current`. Returns new
 * `{ data, current }`; untouched references when nothing matches.
 */
export const applyVisibilityFlags = ({ data, current }, clientId, flags) => {
  const patch = pickVisibilityFields(flags);
  const list = Array.isArray(data) ? data : [];
  if (!clientId || Object.keys(patch).length === 0) return { data, current };
  const matches = (conversation) => conversation && conversation.id === clientId;
  const nextData = list.some(matches)
    ? list.map((c) => (matches(c) ? { ...c, ...patch } : c))
    : data;
  const nextCurrent = matches(current) ? { ...current, ...patch } : current;
  return { data: nextData, current: nextCurrent };
};

// --- GraphQL field gate ------------------------------------------------------
// The GraphQL documents are built in utils/ that have no access to Redux. The
// store is attached from redux/store.js (same pattern as
// utils/backendOverride.attachBackendOverrideStore) and read at query time, so
// the gate follows the live user without a second copy of the flag.
//
// Why the flag and not a schema probe (utils/provenanceProbe): `enabled` is
// computed by the same backend that serves the GraphQL schema, so a backend
// that says `enabled: true` has the fields. With the flag off (or no store,
// as in tests) the documents are byte-identical to the pre-feature ones.

let storeRef = null;

export function attachConversationVisibilityStore(store) {
  storeRef = store || null;
}

export function shouldSelectVisibilityFields() {
  try {
    const userData = storeRef && storeRef.getState()?.user?.data;
    return isConversationVisibilityEnabled(userData);
  } catch (e) {
    return false;
  }
}
